import dayjs from "dayjs";
import { ButtonPassThroughMethodOptions } from "primereact/button";
import {
  CalendarDateTemplateEvent,
  CalendarPassThroughMethodOptions,
  CalendarPassThroughOptions,
} from "primereact/calendar";
import { classNames } from "primereact/utils";

import { focusHoverField, invalidField } from "./common";
const TRANSITIONS = {
  overlay: {
    timeout: 150,
    classNames: {
      enter: "opacity-0 scale-75",
      enterActive:
        "opacity-100 !scale-100 transition-transform transition-opacity duration-150 ease-in",
      exit: "opacity-100",
      exitActive: "!opacity-0 transition-opacity duration-150 ease-linear",
    },
  },
};

export const calendar: Partial<CalendarPassThroughOptions> = {
  root: () => ({
    className: classNames("inline-flex relative rounded"),
  }),
  input: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    root: (param: any) => {
      const invalid = param.props.invalid || param.parent?.props.invalid;
      const variant = param.props.variant ?? param?.parent?.props.variant;
      const disabled = param.props.disabled ?? param?.parent?.props.disabled;

      return {
        className: classNames(
          "text-urbe-text text-sm bg-white p-3 border border-urbe-border transition-colors duration-200 appearance-none",
          focusHoverField,
          {
            "bg-urbe-primaryLight": variant === "filled",
            [invalidField]: invalid,
            "bg-urbe-grayLight select-none pointer-events-none cursor-default": disabled,
          },
        ),
      };
    },
  },
  dropdownButton: {
    root: ({ props }: ButtonPassThroughMethodOptions) => ({
      className: classNames({ "rounded-l-none": props.icon }),
    }),
  },
  panel: ({ props }: CalendarPassThroughMethodOptions) => ({
    className: classNames("bg-white rounded-md", " w-80", {
      "shadow-md border-0 absolute": !props.inline,
      "inline-block overflow-x-auto border border-gray-300 p-2 rounded-lg": props.inline,
    }),
  }),
  header: {
    className: classNames(
      "flex items-center justify-between text-sm",
      "p-1 text-rdc-primary  bg-white m-0 rounded-t-lg",
    ),
  },
  tableHeaderCell: {
    className: classNames("text-center place-content-center	"),
  },
  previousButton: {
    className: classNames(
      "flex items-center justify-center cursor-pointer overflow-hidden relative",
      "w-8 h-8 text-rdc-primary border-0 bg-transparent rounded-full transition-colors duration-200 ease-in-out",
      "hover:text-white hover:border-transparent hover:bg-rdc-primary",
    ),
  },
  nextButton: {
    className: classNames(
      "flex items-center justify-center cursor-pointer overflow-hidden relative",
      "w-8 h-8 text-rdc-primary  border-0 bg-transparent rounded-full transition-colors duration-200 ease-in-out",
      "hover:text-white hover:border-transparent hover:bg-rdc-primary",
    ),
  },
  monthTitle: {
    className: classNames(
      "text-rdc-primary text-[0.875rem] rounded transition duration-200 font-normal p-1 px-2",
      "mr-2",
      "hover:text-white hover:border-transparent hover:bg-rdc-primary",
    ),
  },

  yearTitle: {
    className: classNames(
      "text-rdc-primary text-[0.875rem] rounded transition duration-200 font-normal p-1 px-2",
      "hover:text-white hover:border-transparent hover:bg-rdc-primary",
    ),
  },
  weekDay: {
    className: classNames("text-rdc-gray text-[0.7rem] font-normal transition duration-200 p-1"),
  },
  monthPicker: {
    className: classNames("text-sm"),
  },
  yearPicker: {
    className: classNames("my-2"),
  },
  table: {
    className: classNames("border-collapse w-full", "my-2"),
  },
  tableHeader: {
    className: classNames("text-sm"),
  },
  day: ({ context, props }: CalendarPassThroughMethodOptions) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let classes: any = {
      "*:border *:border-rdc-primary": context.today,
    };
    if (Array.isArray(props.value)) {
      const from = props.value[0];
      const to = props.value[1];
      const ctxtDate = context.date as unknown as CalendarDateTemplateEvent;
      const date = new Date(ctxtDate.year, ctxtDate.month, ctxtDate.day);
      const isTheFirstOnRange = !!from && dayjs(date).isSame(from, "day");
      const isTheLastOnRage = !!to && dayjs(date).isSame(to, "day");
      classes = {
        ...classes,
        "*:bg-rdc-primary *:rounded-full *:text-white": isTheFirstOnRange || isTheLastOnRage,
      };
    }

    return {
      className: classNames(classes),
    };
  },
  dayLabel: ({ context }: CalendarPassThroughMethodOptions) => {
    return {
      className: classNames(
        "w-[28px] h-[28px] rounded-full transition-shadow duration-200 border-transparent border text-[0.81rem] text-rdc-gray",
        "flex items-center justify-center mx-auto overflow-hidden relative",
        "focus:outline-none focus:outline-offset-0 focus:shadow-[0_0_0_0.2rem_rgba(191,219,254,1)] ",
        {
          "opacity-20 cursor-default": context.disabled,
          "cursor-pointer": !context.disabled,
        },
        {
          "text-gray-600 bg-transprent hover:bg-gray-200": !context.selected && !context.disabled,
          "bg-[#E5D9F1] text-rdc-gray hover:bg-rdc-primaryLight hover:text-rdc-gray":
            context.selected && !context.disabled,
        },
        {
          "bg-white border border-rdc-primary": context.today,
        },
      ),
    };
  },
  month: ({ context }: CalendarPassThroughMethodOptions) => ({
    className: classNames(
      "w-1/3 inline-flex items-center justify-center cursor-pointer overflow-hidden relative",
      "p-2 transition-shadow duration-200 rounded-lg",
      "focus:outline-none focus:outline-offset-0 ",
      {
        "text-gray-600 bg-transprent hover:bg-gray-200": !context.selected && !context.disabled,
        "text-white bg-rdc-primary hover:bg-rdc-primaryLight hover:text-gray-800":
          context.selected && !context.disabled,
      },
    ),
  }),

  year: ({ context }: CalendarPassThroughMethodOptions) => ({
    className: classNames(
      "w-1/2 inline-flex items-center justify-center cursor-pointer overflow-hidden relative",
      "p-2 transition-shadow duration-200 rounded-lg",
      "focus:outline-none focus:outline-offset-0",
      {
        "text-gray-600 bg-transprent hover:bg-gray-200": !context.selected && !context.disabled,
        "text-white bg-rdc-primary hover:bg-rdc-primaryLight hover:text-gray-800":
          context.selected && !context.disabled,
      },
    ),
  }),
  timePicker: {
    className: classNames(
      "flex justify-center items-center",
      "border-t-1 border-solid border-gray-300 p-2",
    ),
  },
  incrementButton: {
    className: classNames(
      "flex items-center justify-center cursor-pointer overflow-hidden relative",
      "w-8 h-8 text-gray-600 border-0 bg-transparent rounded-full transition-colors duration-200 ease-in-out",
      "hover:text-gray-700 hover:border-transparent hover:bg-gray-200",
    ),
  },
  decrementButton: {
    className: classNames(
      "flex items-center justify-center cursor-pointer overflow-hidden relative",
      "w-8 h-8 text-gray-600 border-0 bg-transparent rounded-full transition-colors duration-200 ease-in-out",
      "hover:text-gray-700 hover:border-transparent hover:bg-gray-200 ",
    ),
  },
  group: {
    className: classNames(
      "flex-1",
      "border-l border-gray-300 pr-0.5 pl-0.5 pt-0 pb-0",
      "first:pl-0 first:border-l-0",
    ),
  },
  transition: TRANSITIONS.overlay,
};
